<template>
  <div class="offline-success">
    <van-nav-bar :fixed="true" z-index="9" :title="$t('orderPage.offlineSuccessNavTitle')" :border="false" left-arrow @click-left="goHome" />
    <template v-if="orderDetail">
      <section class="offline-success__hero">
        <span class="offline-success__check"><van-icon name="success" /></span>
        <div><h1>{{ $t('orderPage.offlineSuccessTitle') }}</h1><p>{{ $t('orderPage.offlineSuccessDesc') }}</p></div>
      </section>
      <section class="offline-success__card offline-success__product">
        <img v-if="productImage" :src="productImage" :alt="orderDetail.goodsName">
        <div><strong>{{ orderDetail.goodsName }}</strong><span>{{ productSkuText }}</span></div>
      </section>
      <section class="offline-success__card">
        <h2>{{ $t('orderPage.storeDetailsTitle') }}</h2>
        <div v-if="storeLoading" class="offline-success__state">{{ $t('loading') }}</div>
        <div v-else-if="store" class="offline-success__store">
          <van-icon name="location-o" />
          <div class="offline-success__store-copy">
            <small>{{ $t('orderPage.offlineSuccessStoreLabel') }}</small>
            <strong>{{ store.name }}</strong>
            <p>{{ store.address }}</p>
            <p v-if="store.businessHour">{{ $t('orderPage.storeHours') }} · {{ formatBusinessHour(store.businessHour) }}</p>
          </div>
        </div>
        <a v-if="store && store.phone" class="offline-success__phone" :href="`tel:${store.phone}`">
          <van-icon name="phone-o" />
          <span><small>{{ $t('orderPage.storePhone') }}</small><strong>{{ store.phone }}</strong></span>
        </a>
        <div v-else class="offline-success__state offline-success__state--error"><span>{{ $t('orderPage.storeLoadFailed') }}</span><button type="button" @click="loadStore">{{ $t('retry') }}</button></div>
      </section>
      <section class="offline-success__next">
        <h2>{{ $t('orderPage.offlineNextTitle') }}</h2>
        <p><i></i><span>{{ $t('orderPage.offlineNextReview') }}</span></p>
        <p><i></i><span>{{ $t('orderPage.offlineNextPickup') }}</span></p>
      </section>
      <button type="button" class="offline-success__action" @click="viewOrder">{{ $t('orderPage.viewOrder') }}</button>
    </template>
    <div v-else class="offline-success__state offline-success__state--page">{{ loadFailed ? $t('systemError') : $t('loading') }}</div>
  </div>
</template>

<script>
import { Icon } from 'vant';
import { queryOrderDetail } from '@/api/order';
import { getStoreDetail } from '@/api/common';
import { resolveAssetUrl } from '@/common/product';

export default {
  name: 'OfflinePickupSuccess',
  components: { 'van-icon': Icon },
  data() { return { orderId: '', orderDetail: null, store: null, storeLoading: false, loadFailed: false }; },
  computed: {
    productSkuText() {
      const order = this.orderDetail || {};
      const cycle = order.planUnit ? this.$t(`cycle.${order.planUnit}`) : '';
      return [order.skuInfo, order.planIndex, cycle].filter(Boolean).join(' · ');
    },
    productImage() { return resolveAssetUrl(this.orderDetail?.goodsPic || this.orderDetail?.imageUrl || ''); },
    storeId() { return String(this.orderDetail?.storeId || this.$route.query.storeId || '').trim(); }
  },
  mounted() {
    this.orderId = this.$route.query.orderId || '';
    if (!this.orderId) { this.$router.replace('/index'); return; }
    this.loadOrder();
  },
  methods: {
    formatBusinessHour(value) {
      const rawValue = String(value || '').trim();
      const range = rawValue.match(/^(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})$/);
      if (!range) return rawValue;

      const formatTime = (hourText, minuteText) => {
        const hour = Number(hourText);
        const minute = Number(minuteText);
        if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) return '';
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      };

      const start = formatTime(range[1], range[2]);
      const end = formatTime(range[3], range[4]);
      return start && end ? `${start} – ${end}` : rawValue;
    },
    async loadOrder() {
      this.loadFailed = false;
      try {
        const res = await queryOrderDetail({ orderId: this.orderId });
        if (res?.code !== 'Success' || !res.data) throw new Error();
        this.orderDetail = res.data;
        this.loadStore();
      } catch (e) { this.loadFailed = true; }
    },
    async loadStore() {
      if (!this.storeId) return;
      this.storeLoading = true;
      try {
        const res = await getStoreDetail(this.storeId);
        this.store = res?.code === 'Success' ? res.data : null;
      } catch (e) { this.store = null; } finally { this.storeLoading = false; }
    },
    goHome() { this.$router.replace('/index'); },
    viewOrder() { this.$router.replace(`/order/detail?orderId=${this.orderId}`); }
  }
};
</script>

<style lang="less" scoped>
.offline-success { min-height: 100vh; padding: 112px 25px 44px; background: #F6FAFF; box-sizing: border-box; color: #0B0B12; ::v-deep .van-nav-bar { height: 80px; background: #FFF; } ::v-deep .van-nav-bar__title { font-size: 34px; font-weight: 700; color: #0B0B12; } ::v-deep .van-nav-bar__left .van-icon { font-size: 36px; } ::v-deep .van-icon { color: #0B0B12; } }
.offline-success__hero { display: flex; gap: 22px; padding: 28px; border: 1px solid #CFE0FF; border-radius: 18px; background: #EFF6FF; .offline-success__check { display:flex; align-items:center; justify-content:center; flex:0 0 112px; height:112px; border-radius:50%; background:#1664F5; .van-icon { color:#FFF; font-size:62px; } } h1 { margin:0; font-size:30px; line-height:38px; font-weight:700; } p { margin:8px 0 0; color:#65708E; font-size:26px; line-height:36px; } }
.offline-success__card { margin-top:18px; padding:24px; border:1px solid #DDE6F4; border-radius:18px; background:#FFF; h2 { margin:0 0 22px; font-size:30px; line-height:38px; font-weight:700; } }
.offline-success__product { display:flex; align-items:center; gap:20px; img { width:108px; height:108px; object-fit:cover; border-radius:12px; } div { min-width:0; display:flex; flex-direction:column; gap:8px; } strong { font-size:30px; line-height:38px; font-weight:700; } span { color:#65708E; font-size:24px; line-height:32px; } }
.offline-success__store { display:flex; align-items:flex-start; gap:18px; > .van-icon { flex:0 0 76px; width:76px; height:76px; padding-top:14px; border-radius:50%; background:#EDF4FF; color:#1664F5; font-size:48px; text-align:center; box-sizing:border-box; } }
.offline-success__store-copy { flex:1; min-width:0; small, p { display:block; color:#65708E; font-size:26px; line-height:36px; } strong { display:block; margin-top:4px; font-size:30px; line-height:38px; font-weight:700; } p { margin:8px 0 0; } }
.offline-success__phone { display:flex; align-items:center; gap:18px; margin-top:20px; padding-top:20px; border-top:1px solid #DDE6F4; color:#0B0B12; text-decoration:none; > .van-icon { display:flex; align-items:center; justify-content:center; flex:0 0 76px; width:76px; height:76px; border-radius:50%; background:#EDF4FF; color:#1664F5; font-size:44px; } span { display:flex; flex-direction:column; } small { color:#65708E; font-size:26px; line-height:36px; } strong { margin-top:2px; font-size:30px; line-height:38px; font-weight:700; } }
.offline-success__next { margin-top:18px; padding:28px; border-radius:18px; background:#EAF3FF; h2 { margin:0 0 20px; font-size:30px; line-height:38px; font-weight:700; } p { position:relative; display:flex; align-items:flex-start; gap:18px; margin:16px 0; color:#65708E; font-size:26px; line-height:36px; } p + p::before { position:absolute; top:-66px; left:7px; width:2px; height:84px; background:#77B0FF; content:''; } p:last-child { margin-bottom:0; } i { position:relative; z-index:1; flex:0 0 16px; width:16px; height:16px; margin-top:11px; border-radius:50%; background:#1664F5; } span { flex:1; } }
.offline-success__state { padding:24px 0; color:#65708E; font-size:26px; text-align:center; &--error { color:#E54747; button { margin-left:16px; border:0; background:transparent; color:#1664F5; font-size:25px; } } &--page { padding-top:200px; } }
.offline-success__action { width:100%; height:88px; margin-top:22px; border:0; border-radius:14px; background:#1664F5; color:#FFF; font-size:30px; font-weight:700; }
</style>
